/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import { StudioRole } from './studio';

export enum InvitationStatus {
  PENDING = 'pending',
  DECLINED = 'declined',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
}

export interface Invitation {
  id: string;
  email: string;
  role: StudioRole;
  status: InvitationStatus;
  message?: string;
  sentAt: Date;
  expiresAt: Date;
  acceptedAt?: Date;
  declinedAt?: Date;
  cancelledAt?: Date;
  studioId: string;
  studioName: string;
  // Backend returns these as direct properties, not nested
  firstName: string;
  lastName: string;
  invited_by_name: string;
  // Backend soft-delete field (used for transformation)
  deleted_at?: string;
}

export interface InvitationListParams {
  studioId: string;
  search?: string;
  status?: InvitationStatus;
  role?: StudioRole;
  page?: number;
  pageSize?: number;
}

export interface SendInvitationRequest {
  studioId: string;
  email: string;
  role: StudioRole;
  message?: string;
}

export interface InvitationFormData {
  email: string;
  role: StudioRole;
  message: string;
}

export interface InvitationMetrics {
  total: number;
  pending: number;
  accepted: number;
  declined: number;
  expired: number;
  cancelled: number;
  acceptance_rate: number;
}

// Component prop interfaces
export interface InviteMemberFormProps {
  studioId: string;
  onSuccess?: (invitation: Invitation) => void;
  onCancel?: () => void;
  open?: boolean;
  currentMemberCount?: number;
}

export interface InvitationListProps {
  studioId: string;
  searchParams?: InvitationListParams;
  onInvitationUpdate?: (invitation: Invitation) => void;
}

export interface InvitationCardProps {
  invitation: Invitation;
  onResend?: (invitationId: string) => void;
  onCancel?: (invitationId: string) => void;
  onView?: (invitationId: string) => void;
}

export interface InvitationStatusBadgeProps {
  status: InvitationStatus;
  expiresAt?: Date;
  size?: 'small' | 'medium' | 'large';
  showTooltip?: boolean;
}

export interface InvitationPreviewProps {
  invitation: Partial<Invitation>;
  studioName: string;
  studioLogo?: string;
  onSend?: () => void;
  onEdit?: () => void;
  loading?: boolean;
}

// Invitation Response Interfaces (for public token-based responses)
export interface InvitationDetail {
  email: string;
  role: string;
  role_display: string;
  studio_name: string;
  studio_description: string;
  studio_logo?: string;
  invited_by_name: string;
  invitation_expires_at: string;
  expires_in_days: number;
  is_expired: boolean;
  is_valid: boolean;
}

export interface InvitationAcceptRequest {
  first_name?: string;
  last_name?: string;
  password?: string;
}

export interface InvitationDeclineRequest {
  feedback?: string;
}

export interface InvitationResponse {
  id: string;
  email: string;
  role: string;
  role_display: string;
  status: string;
  studio_name: string;
  user_name: string;
  activated_at: string;
}

// Component prop interfaces for invitation response pages
export interface InvitationDetailPageProps {
  token: string;
}

export interface InvitationAcceptFormProps {
  invitation: InvitationDetail;
  token: string;
  onSuccess: (response: InvitationResponse) => void;
  onError: (error: string) => void;
  loading?: boolean;
}

export interface InvitationDeclineFormProps {
  invitation: InvitationDetail;
  token: string;
  onSuccess: () => void;
  onError: (error: string) => void;
  onBack: () => void;
  loading?: boolean;
}

// Error handling interfaces
export interface InvitationError {
  message: string;
  type: 'invalid_token' | 'expired' | 'network' | 'validation' | 'server';
  details?: Record<string, string[]>;
}

// Django REST Framework validation error format
export interface InvitationValidationError {
  email?: string[];
  role?: string[];
  message?: string[];
  non_field_errors?: string[];
}

// RTK Query error format for invitation operations
export interface InvitationApiError {
  status: number;
  data: {
    message?: string;
    detail?: string;
    email?: string[];
    role?: string[];
    custom_message?: string[];
    non_field_errors?: string[];
  };
}

// Helper type for form field errors
export interface InvitationFormErrors {
  email?: string;
  role?: string;
  message?: string;
  general?: string;
}

// Error message constants for common scenarios
export const INVITATION_ERROR_MESSAGES = {
  ALREADY_MEMBER: 'This person is already a member of this studio.',
  PENDING_INVITATION:
    'A pending invitation already exists for this email address.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  ROLE_PERMISSION: 'You cannot assign this role.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  GENERIC_ERROR: 'Failed to send invitation. Please try again.',
} as const;
